import { type ReactNode, useRef } from 'react';
import type { FieldMetadata, ObjectMetadata } from '../../../data/client';
import { cx, PanEdges, ScrollEdge, Tooltip, usePanEdges } from '../../../ui';
import { PanePinned } from '../../shell/PaneHost';
import { PlaceholderRow, RecordTableRow } from './RecordTableRow';
import { useRowSelection } from './RowSelectionProvider';
import type { TableColumn } from './tableModel';
import { useClippedText } from './useClippedText';
import { usePresentRows } from './usePresentRows';

/**
 * Desktop rows inside A's pane host. The host scrolls vertically under the toolbar;
 * TableSurface pans columns horizontally and pins their synchronized header outside
 * that horizontal overflow box. Column widths come from the existing table model.
 * doc: docs/decisions/ui-foundation-w5.md A0.
 * hig: https://developer.apple.com/design/human-interface-guidelines/lists-and-tables
 */
export interface RecordTableProps {
  readonly object: ObjectMetadata;
  readonly columns: readonly TableColumn[];
  /** The fields this VIEW shows, for the row menu's stage submenu (F2). See `RecordTableRow`. */
  readonly fields: readonly FieldMetadata[];
  /** The view's board field: what "stage" means on this object, for the row menu. */
  readonly boardGroupFieldId: string | null;
  /** `undefined` while the page loads: placeholder rows keep the same geometry. */
  readonly recordIds: readonly string[] | undefined;
  /** D21's Archived segment: every row here is a tombstone and carries its own exit. */
  readonly archived?: boolean | undefined;
  /** The query's count and paging controls follow the rows inside the pane host. */
  readonly footer?: ReactNode;
}

/** Keys, not a count: a list index is not an identity, and lint says so too. */
const PLACEHOLDER_ROWS = ['1', '2', '3', '4', '5', '6', '7', '8'] as const;

/**
 * THE COLUMN WIDTHS, exported because the GROUPED table (group-by-aggregation.md D22)
 * renders its own `<table>` and must fix them the same way: a `<tfoot>` whose cells came
 * from a second width rule would let a number drift out from under its own column, and a
 * currency cell is one line per code, so the cell's HEIGHT depends on the data while its
 * width must not.
 */
export function TableColgroup({ columns }: { readonly columns: readonly TableColumn[] }) {
  return (
    <colgroup>
      {columns.map((column) => (
        <col key={column.field.id} style={{ width: `${column.width}rem` }} />
      ))}
    </colgroup>
  );
}

/**
 * A COLUMN HEAD CLIPS INSIDE ITS OWN COLUMN (wave 5 round 5, C6-3; the owner read
 * "Current revenue (monthly)Next payment due" at 1440, which is two heads with no gap
 * because the first one never ends).
 *
 * WHAT WAS WRONG. The cell BOX is fixed - `TableColgroup` writes the column's rem width and
 * both tables are `table-fixed` - and the body has clipped inside that box since wave 3
 * (`RecordTableCell`, `truncate`). The head did not: `.list-head th` is `white-space: nowrap`
 * with overflow VISIBLE, so a label wider than its column simply painted on over the next
 * one. Nothing about the owner's drag caused it; the divider resizes the PANE and the columns
 * keep their widths and pan (`index.css` `.split-divider`), so the head was doing this at
 * every width and the narrower pane is only where he met it.
 *
 * APPLE'S RULE FOR THIS TABLE, twice: "a value longer than its column truncates in the middle
 * for identifiers and at the end otherwise" and, in the Full state, "headings truncate in the
 * middle" (`crm-design/spec/screens/list.md` section 4; hig:
 * https://developer.apple.com/design/human-interface-guidelines/lists-and-tables).
 *
 * IT TRUNCATES AT THE END, NOT IN THE MIDDLE, and that is a deviation with its search
 * written down (ledger B5-1): the product has no middle-truncation construction at all -
 * `truncate` (the Tailwind utility this file's cells already use) and `text-overflow:
 * ellipsis` are end truncation, and `ui/Chart/chartModel.ts` `truncateLabel` is end
 * truncation for an axis label. Inventing one for the heads would be a second truncation
 * language in a table whose cells use the first.
 */
function HeadLabel({
  column,
  clipped,
}: {
  readonly column: TableColumn;
  readonly clipped: boolean;
}) {
  const label = (
    <span data-head-label={column.field.id} className="block truncate">
      {column.field.label}
    </span>
  );
  /* THE FULL LABEL STAYS REACHABLE, in the checkpoint head's own construction one block
     down: what is cut off is on the help tag. The accessible name is not this copy's job -
     the visible head is inside an `aria-hidden` pan viewport and the head a screen reader
     reads is the body table's `accessibleOnly` one, which carries every label in full. */
  return clipped ? <Tooltip label={column.field.label}>{label}</Tooltip> : label;
}

/**
 * The sticky column head, shared with the grouped table for the same reason the colgroup
 * is: two spellings of one header is how a grouped table starts looking like a different
 * product from the flat one.
 */
export function TableHead({
  columns,
  accessibleOnly = false,
}: {
  readonly columns: readonly TableColumn[];
  readonly accessibleOnly?: boolean;
}) {
  const row = useRef<HTMLTableRowElement>(null);
  const clipped = useClippedText(row, 'data-head-label', !accessibleOnly);
  return (
    // The visible header is pinned by PanePinned; the body's copy carries table semantics.
    <thead className={accessibleOnly ? 'sr-only' : 'list-head relative isolate'}>
      <tr ref={row}>
        {columns.map((column, index) => (
          <th
            key={column.field.id}
            scope="col"
            className={cx(
              // `data-head`/`data-cell` carry the padding, so density is a token decision
              // instead of three components to edit.
              'data-head',
              column.align === 'right' ? 'text-right' : 'text-left',
            )}
          >
            {/*
              THE HEAD IS A FIXED EDGE OVER SCROLLING CONTENT, SO IT CARRIES THE EFFECT
              (W5 B-1, review check (u)). "A scroll edge effect provides a visual
              separation between certain interface elements, such as toolbars, and the
              scrolling content area behind them" and "Only use a scroll edge effect when
              a scroll view is behind floating interface elements" (hig:
              https://developer.apple.com/design/human-interface-guidelines/scroll-views).
              The head is pinned at the scrollport's top edge and the rows pass under it,
              which is exactly that case; it had an opaque canvas fill instead, which is a
              wall rather than a separation.

              HARD, LIKE THE WINDOW TOOLBAR ON THE DESKTOP: "Hard is mostly used on macOS
              ... ideal for interactive text, controls without backgrounds, or PINNED TABLE
              HEADERS that need extra clarity" (wwdc:
              https://developer.apple.com/videos/play/wwdc2025/356/ at 11:18; kit masters
              `Scroll Edge Effect/Light - Top - Hard` and `Dark - Top - Hard`).

              ONE PER VIEW: the effect belongs to the TABLE's scroller, and `TableHead` is
              the one head both the flat and the grouped table render, so there is exactly
              one instance on the screen either way.

              WHY IT SITS INSIDE THE FIRST CELL. A `<div>` is flow content and valid inside
              a `<th>`, and invalid inside a `<thead>`. Its containing block is still the
              head: the cells are static, the `<thead>` is the only positioned ancestor, and
              `offsetParent` of an absolutely positioned element skips the table's static
              td/th/table rule - so the effect fills the head's box exactly, which is what
              review check (t) asserts.
            */}
            {!accessibleOnly && index === 0 && <ScrollEdge host="column-head" style="hard" />}
            {/*
              A CHECKPOINT HEAD IS ITS INITIALS (`phase-b-coach.md` D5), and the full label is
              reachable TWICE, because a tooltip is a mouse affordance and this repository's
              own Tooltip says so: the hover panel for a pointer, and a visually hidden span
              for the accessible name the column announces to everybody else. The initials are
              `aria-hidden`, because "WS" read out loud is two letters rather than a column.
            */}
            {column.headerAbbreviation === undefined ? (
              <HeadLabel column={column} clipped={clipped.has(column.field.id)} />
            ) : (
              <Tooltip label={column.field.label}>
                <span className="inline-flex">
                  <span aria-hidden="true">{column.headerAbbreviation}</span>
                  <span className="sr-only">{column.field.label}</span>
                </span>
              </Tooltip>
            )}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/**
 * Horizontal panning and keyboard selection, without a nested vertical scrollport.
 * The visible header is outside overflow-x, so its sticky owner is PaneHost.
 * Its aria-hidden copy shares exact widths with the accessible body table.
 * doc: docs/decisions/ui-foundation-w5.md A0; W5-B2 host record.
 */
export function TableSurface({
  label,
  columns,
  busy,
  footer,
  children,
}: {
  /** The scroll region's accessible name; a keyboard reader lands on it before the rows. */
  readonly label: string;
  readonly columns: readonly TableColumn[];
  readonly busy: boolean;
  readonly footer?: ReactNode;
  readonly children: ReactNode;
}) {
  const scroller = useRef<HTMLElement>(null);
  const header = useRef<HTMLDivElement>(null);
  const edges = usePanEdges(scroller);
  const selection = useRowSelection();

  return (
    <div aria-busy={busy ? true : undefined} className="flex min-w-0 shrink-0 flex-col">
      {/* doc: docs/decisions/ui-foundation-w5.md A0. The sticky header must be outside
          the horizontal overflow box, which otherwise traps its sticky positioning.
          Both tables use the same colgroup. Only the body table exposes column semantics. */}
      <PanePinned>
        {/* THE ONE BOX ON THIS SCREEN THAT IS MEANT TO CLIP (wave 5, B2-1). It is the column
            head's viewport onto a table as wide as the body's, panned by the body's own
            `scrollLeft` above, so its content is wider than its box whenever the table is -
            which R2 reads as text somebody cannot finish. There is nothing to read here: the
            row is `aria-hidden` and the head a screen reader uses is the body table's own. */}
        <div
          ref={header}
          className="overflow-hidden"
          aria-hidden="true"
          data-list-column-pan
          data-measure-ignore="clipped-text:the column head is a viewport onto the body's own table width and pans with it, so content past its edge is the horizontal pan working; this copy is aria-hidden and the read head is the body table's"
        >
          <table
            className="min-w-full table-fixed cursor-default border-collapse text-sm"
            style={{ width: `${columns.reduce((sum, column) => sum + column.width, 0)}rem` }}
          >
            <TableColgroup columns={columns} />
            <TableHead columns={columns} />
          </table>
        </div>
      </PanePinned>
      {/* THE STRIPS ARE SIBLINGS OF THE SCROLLER, not children of it: a strip inside the
          scrolling element pans away with the content it is guarding. This wrapper is
          their containing block and is exactly the scrollport's box, so the shading stops
          at the footer row rather than running over it. */}
      {/*
        `min-w-0` ON BOTH BOXES, and it is the half the card used to hide. A flex item's
        default `min-width` is `auto`, so the scrolling section refused to shrink below the
        table's min-content and OVERFLOWED its pane; the card's `overflow-hidden` clipped
        that and nobody saw it. With the card gone the rows painted under the detail pane,
        which the first review round found as twelve dates reported covered by the pane's
        own placeholder. The scroller is what must be narrow enough to scroll.
      */}
      <div className="relative min-w-0">
        <section
          ref={scroller}
          onScroll={(event) => {
            if (header.current !== null) header.current.scrollLeft = event.currentTarget.scrollLeft;
          }}
          // A scrolling region that cannot be focused cannot be scrolled by keyboard, and
          // this one pans SIDEWAYS: no cell is a tab stop, so without this the columns past
          // the fold would be unreachable without a pointer. Same answer as `KanbanColumn`.
          // biome-ignore lint/a11y/noNoninteractiveTabindex: the region is a scroll container
          tabIndex={0}
          aria-label={label}
          onKeyDown={(event) => {
            /**
             * ARROWS MOVE, SHIFT EXTENDS, CMD-A TAKES THE LOADED ROWS, ESCAPE CLEARS - and
             * every one of them is ignored while the key came from inside a control, so a
             * cell editor keeps its own arrow keys and its own Escape.
             */
            const inControl =
              event.target instanceof Element &&
              event.target !== event.currentTarget &&
              event.target.closest(
                'a,button,input,select,textarea,[role=menu],[role=dialog],[role=listbox]',
              ) !== null;
            if (inControl) return;
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              selection.move(event.key === 'ArrowDown' ? 1 : -1, event.shiftKey);
              return;
            }
            if (event.key === 'a' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              selection.selectAll();
              return;
            }
            if (event.key === 'Escape') selection.clear();
          }}
          // `relative`: see the file header. An `EmptyValue`'s visually-hidden span is
          // absolutely positioned, and one whose containing block is outside this scroller
          // pushes the whole document sideways instead of being clipped by it.
          className="relative min-w-0 overflow-x-auto focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
        >
          {children}
        </section>
        <PanEdges start={edges.start} end={edges.end} />
      </div>
      {footer}
    </div>
  );
}

export function RecordTable({
  object,
  columns,
  fields,
  boardGroupFieldId,
  recordIds,
  archived = false,
  footer,
}: RecordTableProps) {
  const loading = recordIds === undefined;
  const presence = usePresentRows(recordIds ?? []);

  return (
    <TableSurface label={object.labelPlural} columns={columns} busy={loading} footer={footer}>
      {/*
        THE ARROW OVER THE WHOLE TABLE (wave 4, and L-B1's rule one row further up). The row
        and its cells declare `cursor: default` in the stylesheet, but the `<table>` and its
        COLUMN HEADS did not: they stood at `cursor: auto`, which over a heading's text is the
        I-beam - the same "als wuerde ich einen text auswaehlen" the owner named on the cells.
        `cursor` inherits, so one declaration on the table covers the heads, the table's own
        box and anything a later column adds; a control inside still takes its own.
        hig: https://developer.apple.com/design/human-interface-guidelines/pointing-devices,
        where the Arrow is "Standard pointer for selecting and interacting with content and
        interface elements" and the horizontal I beam means "Selection and insertion of text
        is possible in a horizontal layout, such as a TextEdit or Pages document".
      */}
      <table
        className="min-w-full table-fixed cursor-default border-collapse text-sm"
        style={{ width: `${columns.reduce((sum, column) => sum + column.width, 0)}rem` }}
      >
        <TableColgroup columns={columns} />
        <TableHead columns={columns} accessibleOnly />
        <tbody>
          {loading
            ? PLACEHOLDER_ROWS.map((key) => <PlaceholderRow key={key} columns={columns} />)
            : presence.ids.map((recordId) => (
                <RecordTableRow
                  key={recordId}
                  object={object}
                  columns={columns}
                  fields={fields}
                  boardGroupFieldId={boardGroupFieldId}
                  recordId={recordId}
                  present={presence.present(recordId)}
                  onExited={() => presence.exited(recordId)}
                  archived={archived}
                />
              ))}
        </tbody>
      </table>
    </TableSurface>
  );
}
